import { BLANK_DISPLAY } from 'chery-kpi-engine';

export interface StackedSeries {
  key: string;
  label: string;
}

export interface StackedColumn {
  weekId: string;
  /** Axis label for the week. */
  label: string;
  segments: readonly { key: string; value: number; from: number; to: number }[];
  /** `null` = no value that week (a gap, not a zero column). */
  total: number | null;
}

export interface StackedColumnsProps {
  series: readonly StackedSeries[];
  columns: readonly StackedColumn[];
  max: number;
  formatValue: (value: number) => string;
  'aria-label': string;
}

const WIDTH = 720;
const HEIGHT = 200;
const PAD = { left: 36, right: 8, top: 10, bottom: 22 };
const GAP = 2;

const seriesColor = (slot: number): string => `var(--series-${slot + 1})`;

/**
 * Weekly stacked columns in inline SVG (plan §2.1): categorical slots in fixed order, a 2px surface gap between
 * segments and between columns, a legend (≥ 2 series), a recessive baseline, and a per-segment hover title. A week
 * with no value is an empty slot, never a zero column.
 */
export const StackedColumns = ({ series, columns, max, formatValue, ...rest }: StackedColumnsProps) => {
  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const slotW = columns.length === 0 ? plotW : plotW / columns.length;
  const barW = Math.max(2, slotW - GAP);
  const top = max > 0 ? max : 1;
  const y = (value: number): number => PAD.top + plotH - (value / top) * plotH;
  const labelEvery = Math.max(1, Math.ceil(columns.length / 12));
  const slotOf = new Map(series.map((each, index) => [each.key, index]));

  return (
    <figure
      className="space-y-2"
      data-stacked-columns
    >
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="block h-auto w-full"
        role="img"
        aria-label={rest['aria-label']}
      >
        <line
          x1={PAD.left}
          x2={WIDTH - PAD.right}
          y1={y(0)}
          y2={y(0)}
          stroke="hsl(var(--border))"
        />
        <text
          x={PAD.left - 4}
          y={y(top)}
          textAnchor="end"
          dominantBaseline="middle"
          fontSize={10}
          fill="hsl(var(--muted-foreground))"
        >
          {max > 0 ? formatValue(max) : BLANK_DISPLAY}
        </text>
        {columns.map((column, index) => {
          const x = PAD.left + index * slotW + GAP / 2;

          return (
            <g
              key={column.weekId}
              data-week={column.weekId}
            >
              {column.segments.map((segment, position) => {
                const isTop = position === column.segments.length - 1;
                const height = Math.max(0, y(segment.from) - y(segment.to) - (position > 0 ? GAP : 0));
                const label = series[slotOf.get(segment.key) ?? 0]?.label ?? segment.key;

                return (
                  <rect
                    key={segment.key}
                    x={x}
                    y={y(segment.to)}
                    width={barW}
                    height={height}
                    rx={isTop ? Math.min(4, barW / 2) : 0}
                    fill={seriesColor(slotOf.get(segment.key) ?? 0)}
                  >
                    <title>{`${column.label} · ${label}: ${formatValue(segment.value)}`}</title>
                  </rect>
                );
              })}
              {index % labelEvery === 0 ? (
                <text
                  x={x + barW / 2}
                  y={HEIGHT - 6}
                  textAnchor="middle"
                  fontSize={9}
                  fill="hsl(var(--muted-foreground))"
                >
                  {column.label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {series.length >= 2 ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {series.map((each, slot) => (
            <li
              key={each.key}
              className="flex items-center gap-1.5"
            >
              <span
                aria-hidden
                className="inline-block size-2.5 rounded-sm"
                style={{ background: seriesColor(slot) }}
              />
              {each.label}
            </li>
          ))}
        </ul>
      ) : null}
    </figure>
  );
};
